import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BITBUCKET_COMMIT,
  isUpdateAvailable,
  VersionPayload,
} from '../config/appVersion';

const VERSION_URL = '/version.json';
const DEBOUNCE_MS = 1000;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as VersionPayload;
    return typeof data.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

/**
 * Checks /version.json on mount and when the tab becomes visible/focused again.
 * No interval polling — returning to the tab surfaces updates immediately after deploy.
 */
export function useAppVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const lastCheckAt = useRef(0);
  const checking = useRef(false);

  const checkVersion = useCallback(async () => {
    if (BITBUCKET_COMMIT === 'dev' || updateAvailable) {
      return;
    }
    const now = Date.now();
    if (checking.current || now - lastCheckAt.current < DEBOUNCE_MS) {
      return;
    }
    checking.current = true;
    lastCheckAt.current = now;
    try {
      const remote = await fetchRemoteVersion();
      if (isUpdateAvailable(BITBUCKET_COMMIT, remote)) {
        setUpdateAvailable(true);
      }
    } finally {
      checking.current = false;
    }
  }, [updateAvailable]);

  useEffect(() => {
    void checkVersion();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };
    const onFocus = () => {
      void checkVersion();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [checkVersion]);

  return updateAvailable;
}
